import { useEffect, useMemo } from 'react';
import { useReduxState } from 'hooks';
import { getMergedOptionGroupOptions } from 'shared-helpers/customAttributes';
import { CalendarEventDateString } from 'components/calendar';

import FilterButton from './FilterButton';
import SavedFilters from './SavedFilters';
import { useFiltersContext, useFiltersDispatch } from './FilterContext';

import type { FilterCompletionStatus, FilterTypes } from 'shared-types';
import type { ButtonInterface } from 'components/common/Button';
import type { IconProp } from 'components/common/icons/Icon';
import type { FilterItem } from '.';

type FilterTypesWithSubItems = Extract<
  FilterTypes,
  'achievements' | 'calendarEvents' | 'competences' | 'courses' | 'paths'
>;

interface Props {
  allowedItemIds?: Partial<
    Record<Extract<FilterTypes, 'competenceProfiles' | 'competences'>, number[]>
  >;
  allowMultiple?: Partial<Record<FilterTypes, boolean>>;
  customFilters?: (FilterItem & { position: number })[];
  filterId: string;
  filters: Partial<
    Record<
      Exclude<FilterTypes, 'userAttributes'>,
      boolean | Partial<FilterItem>
    > & {
      userAttributes: boolean;
    }
  >;
  ignoreSubItems?: Partial<Record<FilterTypesWithSubItems, boolean>>;
  hideSavedFilters?: boolean;
  syncWithUrl?: boolean;
  savedFiltersPreservedKeys?: string[];
  triggerProps?: ButtonInterface;
}

const Filters = ({
  allowedItemIds,
  allowMultiple = {},
  customFilters = [],
  filterId,
  filters = {},
  ignoreSubItems = {},
  hideSavedFilters = false,
  savedFiltersPreservedKeys,
  syncWithUrl = true,
  triggerProps
}: Props) => {
  const {
    language,
    messages,
    serverConfig: { accountAnonymiseOnDeletion },
    user
  } = useReduxState();

  const { data, savedFiltersAllowed } = useFiltersContext();

  const filterDispatch = useFiltersDispatch();

  const isSupervisor = user?.roles.includes('supervisor');
  const showSupervisorFilter = filters.supervisors && data?.supervisors?.length;
  const showMyTeamFilter =
    filters.myTeam && isSupervisor && !showSupervisorFilter;

  const commonCompletionFilters: Record<FilterCompletionStatus, string> = {
    all: messages.commonAll,
    expired: messages.commonExpired,
    expiring: messages.commonExpiring,
    failed: messages.readerCompletionFailed,
    inProgress: messages.commonInProgress,
    no: messages.tasksUsersNotCompleted,
    notStarted: messages.tasksUsersNotStartedSingular,
    valid: messages.commonValid,
    waitingForReviews: messages.completionsWaitingForReviews,
    yes: messages.commonCompleted
  };

  const validFilters = useMemo(() => {
    const defaultFilters: FilterItem[] = [
      ...(filters.accountAccess ||
      filters.accountIdentifier ||
      filters.accountLogin ||
      filters.accountType ||
      (filters.userAttributes && data?.userAttributes?.length)
        ? [
            {
              allowMultiple: false,
              field: 'user',
              icon: 'user' as const,
              selectAllOnClick: false,
              title: messages.commonUser,
              filters: [
                ...(filters.accountType
                  ? [
                      {
                        allowMultiple: true,
                        field: 'accountType',
                        icon: 'user-circle' as const,
                        items: [
                          {
                            label: messages.commonUser,
                            value: 'user'
                          },
                          {
                            label: messages.usersAdmin,
                            value: 'admin'
                          },
                          {
                            label: messages.usersSuperadmin,
                            value: 'superadmin'
                          },
                          {
                            label: messages.usersGuestAccount,
                            value: 'guest'
                          },
                          ...(accountAnonymiseOnDeletion
                            ? [
                                {
                                  label: messages.commonAnonymous,
                                  value: 'anonymous'
                                }
                              ]
                            : []),
                          {
                            label: messages.commonUserExclude,
                            value: 'excludeUser'
                          },
                          {
                            label: messages.usersAdminExclude,
                            value: 'excludeAdmin'
                          },
                          {
                            label: messages.usersSuperadminExclude,
                            value: 'excludeSuperadmin'
                          },
                          {
                            label: messages.usersGuestAccountExclude,
                            value: 'excludeGuest'
                          },
                          ...(accountAnonymiseOnDeletion
                            ? [
                                {
                                  label: messages.commonAnonymousExclude,
                                  value: 'excludeAnonymous'
                                }
                              ]
                            : [])
                        ],
                        title: messages.usersAccountType,
                        ...(typeof filters.accountType !== 'boolean'
                          ? filters.accountType
                          : {})
                      }
                    ]
                  : []),
                ...(filters.accountIdentifier
                  ? [
                      {
                        allowMultiple: true,
                        field: 'accountIdentifier',
                        icon: 'at' as const,
                        items: [
                          {
                            label: messages.usersAccountWithEmail,
                            value: 'withEmail'
                          },
                          {
                            label: messages.usersAccountWithoutEmail,
                            value: 'withoutEmail'
                          }
                        ],
                        title: messages.filtersAccountIdentifier,
                        ...(typeof filters.accountIdentifier !== 'boolean'
                          ? filters.accountIdentifier
                          : {})
                      }
                    ]
                  : []),
                ...(filters.accountAccess
                  ? [
                      {
                        allowMultiple: true,
                        field: 'accountAccess',
                        icon: 'lock' as const,
                        items: [
                          {
                            label: messages.usersAccountLocked,
                            value: 'locked'
                          },
                          {
                            label: messages.usersAccountNotLocked,
                            value: 'notLocked'
                          },
                          {
                            label: messages.usersAccountExpired,
                            value: 'expired'
                          },
                          {
                            label: messages.usersAccountNotExpired,
                            value: 'notExpired'
                          }
                        ],
                        title: messages.filtersAccountAccess,
                        ...(typeof filters.accountAccess !== 'boolean'
                          ? filters.accountAccess
                          : {})
                      }
                    ]
                  : []),
                ...(filters.accountLogin
                  ? [
                      {
                        allowMultiple: true,
                        field: 'accountLogin',
                        icon: 'sign-in-alt' as const,
                        items: [
                          {
                            label: messages.usersAccountHasLoggedIn,
                            value: 'loggedIn'
                          },
                          {
                            label: messages.usersAccountHasNeverLoggedIn,
                            value: 'neverLoggedIn'
                          }
                        ],
                        title: messages.filtersAccountLogin,
                        ...(typeof filters.accountLogin !== 'boolean'
                          ? filters.accountLogin
                          : {})
                      }
                    ]
                  : []),
                ...(filters.userAttributes && data?.userAttributes?.length
                  ? data?.userAttributes?.map((attribute) => {
                      const { max, min, type } = attribute;

                      const filterData: FilterItem = {
                        allowMultiple: allowMultiple['userAttributes'],
                        field: attribute.key,
                        icon: 'tag' as const,
                        title:
                          attribute.titleTranslations?.[language] ||
                          attribute.title
                      };

                      if (type === 'Boolean') {
                        filterData.items = [
                          { label: messages.commonYes, value: 'true' },
                          { label: messages.commonNo, value: 'false' },
                          { label: messages.commonNotSet, value: 'not set' },
                          {
                            label: messages.userAttributesNoIncludingNotSet,
                            value: 'no including not set'
                          }
                        ];
                      } else if (type === 'Number') {
                        filterData.itemsAll = {
                          label: messages.groupsUserAttributesAny,
                          value: 'anyValueAllowed'
                        };
                        filterData.items = Array.from(
                          Array(Math.max(max - min + 1, 1)),
                          (n, i) => ({
                            label: `${i + min}`,
                            value: `${i + min}`
                          })
                        );
                        filterData.selectAllOnClick = true;
                      } else if (
                        type === 'ArrayWithOptions' ||
                        type === 'StringWithOptions'
                      ) {
                        const mergedOptions = getMergedOptionGroupOptions({
                          language,
                          optionGroups: attribute.optionGroups
                        });
                        filterData.itemsAll = {
                          label: messages.groupsUserAttributesAny,
                          value: 'anyValueAllowed'
                        };
                        filterData.items = mergedOptions.map((option) => ({
                          label: option.label,
                          value: option.key
                        }));
                        filterData.selectAllOnClick = true;
                      } else if (
                        type === 'String' ||
                        type === 'Array' ||
                        type === 'Text'
                      ) {
                        filterData.input = true;
                      }

                      return filterData;
                    })
                  : [])
              ]
            }
          ]
        : []),
      ...(filters.groups && data?.groups?.length
        ? [
            {
              allowMultiple: allowMultiple['groups'],
              field: 'groupId',
              icon: 'users' as const,
              items: data.groups.map((group) => ({
                label: group.title,
                value: `${group.id}`
              })),
              title: messages.commonGroup,
              ...(typeof filters.groups !== 'boolean' ? filters.groups : {})
            }
          ]
        : []),
      ...(showSupervisorFilter
        ? [
            {
              allowMultiple: allowMultiple['supervisors'],
              field: 'supervisorId',
              icon: 'sitemap' as const,
              items: [
                ...(isSupervisor
                  ? [
                      {
                        label: messages.myTeamPage,
                        value: `${user?.id}`
                      }
                    ]
                  : []),
                ...(data?.supervisors?.map((supervisor) => ({
                  label: supervisor.user,
                  value: `${supervisor.id}`
                })) || [])
              ],
              title: messages.adminSupervisor,
              ...(typeof filters.supervisors !== 'boolean'
                ? filters.supervisors
                : {})
            }
          ]
        : []),
      ...(showMyTeamFilter
        ? [
            {
              field: 'supervisorId',
              icon: 'sitemap' as const,
              items: [
                {
                  label: messages.adminSupervisorMyTeamOnly,
                  value: `${user?.id}`
                }
              ],
              title: messages.myTeamPage,
              ...(typeof filters.myTeam !== 'boolean' ? filters.myTeam : {})
            }
          ]
        : []),
      ...(filters.courses && data?.courses?.length
        ? [
            {
              allowMultiple: allowMultiple['courses'],
              field: 'courseId',
              icon: 'book-open' as const,
              items: data.courses.map((course) => ({
                label: course.title,
                value: `${course.id}`,
                ...(ignoreSubItems?.courses
                  ? {}
                  : {
                      subItemsAll: {
                        label: messages.commonAll,
                        value: 'all'
                      },
                      subItems: [
                        'yes',
                        'valid',
                        'expiring',
                        'expired',
                        'failed',
                        'waitingForReviews',
                        'inProgress',
                        'notStarted',
                        'no'
                      ].map((status) => ({
                        label: commonCompletionFilters[status],
                        value: status
                      }))
                    })
              })),
              selectAllOnClick: true,
              title: messages.commonCourse,
              ...(typeof filters.courses !== 'boolean' ? filters.courses : {})
            }
          ]
        : []),
      ...(filters.paths && data?.paths?.length
        ? [
            {
              allowMultiple: allowMultiple['paths'],
              field: 'pathId',
              icon: 'tasks' as const,
              items: data.paths.map((path) => ({
                label: path.title,
                value: `${path.id}`,
                ...(ignoreSubItems?.paths
                  ? {}
                  : {
                      subItemsAll: {
                        label: messages.commonAll,
                        value: 'all'
                      },
                      subItems: ['yes', 'no'].map((status) => ({
                        label: commonCompletionFilters[status],
                        value: status
                      }))
                    })
              })),
              selectAllOnClick: true,
              title: messages.path,
              ...(typeof filters.paths !== 'boolean' ? filters.paths : {})
            }
          ]
        : []),
      ...(filters.achievements && data?.achievements?.length
        ? [
            {
              allowMultiple: allowMultiple['achievements'],
              field: 'achievementId',
              icon: 'check-circle' as const,
              items: data.achievements.map((achievement) => ({
                label: achievement.title,
                value: `${achievement.id}`,
                ...(ignoreSubItems?.achievements
                  ? {}
                  : {
                      subItemsAll: {
                        label: messages.commonAll,
                        value: 'all'
                      },
                      subItems: [
                        'yes',
                        'valid',
                        'expiring',
                        'expired',
                        'no'
                      ].map((status) => ({
                        label: commonCompletionFilters[status],
                        value: status
                      }))
                    })
              })),
              selectAllOnClick: true,
              title: messages.achievement,
              ...(typeof filters.achievements !== 'boolean'
                ? filters.achievements
                : {})
            }
          ]
        : []),
      ...(filters.calendarEvents && data?.calendarEvents?.length
        ? [
            {
              allowMultiple: allowMultiple['calendarEvents'],
              field: 'calendarEventId',
              icon: ['far', 'calendar-days'] as IconProp,
              items: data.calendarEvents.map((calendarEvent) => ({
                label: calendarEvent.title,
                labelSubtitle: (
                  <CalendarEventDateString
                    startDate={calendarEvent.startDate as string}
                    endDate={calendarEvent.endDate as string}
                  />
                ),
                value: `${calendarEvent.id}`,
                ...(ignoreSubItems?.calendarEvents
                  ? {}
                  : {
                      subItemsAll: {
                        label: messages.commonAll,
                        value: 'all'
                      },
                      subItems: [
                        {
                          label: messages.eventCalendarParticipantParticipated,
                          value: 'participated'
                        },
                        {
                          label:
                            messages.eventCalendarParticipantNotParticipated,
                          value: 'notParticipated'
                        }
                      ]
                    })
              })),
              selectAllOnClick: true,
              title: messages.eventCalendarEvent,
              ...(typeof filters.calendarEvents !== 'boolean'
                ? filters.calendarEvents
                : {})
            }
          ]
        : []),
      ...(filters.competences && data?.competences?.length
        ? [
            {
              allowMultiple: allowMultiple['competences'],
              field: 'competenceId',
              icon: 'medal' as const,
              items:
                data.competences
                  .filter(
                    (competence) =>
                      !allowedItemIds?.competences ||
                      allowedItemIds.competences.includes(competence.id)
                  )
                  .sort((a, b) =>
                    (a.nameLocales?.[language] || a.name) >
                    (b.nameLocales?.[language] || b.name)
                      ? 1
                      : -1
                  )
                  .map((competence) => ({
                    label:
                      competence.nameLocales?.[language] || competence.name,
                    value: `${competence.id}`,
                    ...(ignoreSubItems?.competences
                      ? {}
                      : {
                          subItemsAll: {
                            label: messages.competenciesAllLevels,
                            value: 'all'
                          },
                          subItems: competence.levels?.length
                            ? [
                                ...competence.levels.map((level) => ({
                                  label:
                                    level.nameLocales?.[language] || level.name,
                                  value: `${level.id}`
                                }))
                              ]
                            : []
                        })
                  })) || [],
              selectAllOnClick: true,
              title: messages.commonCompetence,
              ...(typeof filters.competences !== 'boolean'
                ? filters.competences
                : {})
            }
          ]
        : []),
      ...(filters.competenceProfiles && data?.competenceProfiles?.length
        ? [
            {
              allowMultiple: allowMultiple['competenceProfiles'],
              field: 'competenceProfileId',
              icon: 'address-card' as const,
              items:
                data.competenceProfiles
                  .filter(
                    (profile) =>
                      !allowedItemIds?.competenceProfiles ||
                      allowedItemIds.competenceProfiles.includes(profile.id)
                  )
                  .sort((a, b) =>
                    (a.titleLocales?.[language] || a.title) >
                    (b.titleLocales?.[language] || b.title)
                      ? 1
                      : -1
                  )
                  .map((profile) => ({
                    label: profile.titleLocales?.[language] || profile.title,
                    value: `${profile.id}`
                  })) || [],
              title: messages.competencyProfile,
              ...(typeof filters.competenceProfiles !== 'boolean'
                ? filters.competenceProfiles
                : {})
            }
          ]
        : []),
      ...(filters.ambitions && data?.ambitions?.length
        ? [
            {
              allowMultiple: allowMultiple['ambitions'],
              field: 'ambitionId',
              icon: 'flag' as const,
              items: data.ambitions.map((ambition) => ({
                label: ambition.title,
                value: `${ambition.id}`
              })),
              title: messages.commonAmbition,
              ...(typeof filters.ambitions !== 'boolean'
                ? filters.ambitions
                : {})
            }
          ]
        : [])
    ];

    if (customFilters?.length) {
      customFilters
        .sort((a, b) => a.position - b.position)
        .forEach((customFilter) => {
          defaultFilters.splice(customFilter.position, 0, customFilter);
        });
    }

    return defaultFilters;
  }, [allowedItemIds, data, filters, messages]);

  /**
   * Content signature for `validFilters` — parent often passes inline `filters` / `allowMultiple`
   * objects, so `validFilters` is a new array reference every render even when unchanged.
   * Depending on that reference would re-dispatch `updateFilters` every render and loop when
   * the parent also uses `useAppliedFilterParams` (context subscriber).
   */
  const validFiltersSignature = useMemo(() => {
    const sigFromFilterItem = (item: FilterItem) => {
      return `{${item.allowMultiple || false}|${item.field}|${
        item.items
          ?.map(
            ({ label, value, subItemsAll, subItems }) =>
              `${label}|${value}|${subItemsAll?.label || ''}|${
                subItemsAll?.value || ''
              }|${
                subItems
                  ?.map(({ label, value }) => `${label}|${value}`)
                  .join('|') || ''
              }`
          )
          .join('|') || ''
      }|${item.itemsAll?.label || ''}|${item.itemsAll?.value || ''}|${
        item.selectAllOnClick || false
      }|${item.title}|${item.input || false}}`;
    };

    return validFilters.reduce((acc, item) => {
      acc += sigFromFilterItem(item);
      if (item.filters?.length) {
        item.filters.forEach((subItem) => {
          acc += sigFromFilterItem(subItem);
        });
      }

      return acc;
    }, '');
  }, [validFilters]);

  useEffect(() => {
    filterDispatch({
      type: 'updateFilters',
      filters: validFilters,
      filterId,
      syncWithUrl
    });
  }, [validFiltersSignature, filterId, syncWithUrl]);

  const effectiveHideSavedFilters = hideSavedFilters || !syncWithUrl;

  return (
    <>
      <FilterButton filterId={filterId} triggerProps={triggerProps} />

      {savedFiltersAllowed && !effectiveHideSavedFilters && (
        <div style={{ display: 'inline-block', marginLeft: '8px' }}>
          <SavedFilters preservedFilterKeys={savedFiltersPreservedKeys} />
        </div>
      )}
    </>
  );
};

export default Filters;
